import type { FC } from 'react'
import { FFInput } from '~/shared/ui'
import { useState, useEffect, useEffectEvent } from 'react'
import { Box, InputAdornment, Tooltip } from '@mui/material'
import { haveChanged } from '~/shared/lib/haveChanged'
import { nextErrorText } from './utils'

interface VisitMixEditorProps {
  groupTitle: string
  mappedAppointmentTypes: string[]
  visitMixPercentage: number | null
  displayError: boolean
  totalError: boolean
  disabled?: boolean
  disabledTooltip?: string
  onChange: (visitMixPercentage: number) => void
  onError: (error: boolean) => void
}

const VisitMixEditor: FC<VisitMixEditorProps> = props => {
  const {
    visitMixPercentage,
    groupTitle,
    mappedAppointmentTypes,
    displayError,
    totalError,
    disabled,
    disabledTooltip,
  } = props
  const [boundErrorText, setBoundErrorText] = useState<string>('')

  // The parent passes an inline `onError`, so the effect reads it as an event, not a dependency.
  const reportError = useEffectEvent((error: boolean) => props.onError(error))

  let problem: string | null = null
  if (visitMixPercentage !== null && (visitMixPercentage > 100 || visitMixPercentage < 0)) {
    problem = 'Between 0 and 100'
  } else if (visitMixPercentage !== null && totalError) {
    problem = 'Total must equal 100%'
  }
  const validationSource = [visitMixPercentage, displayError, totalError]
  const [validatedFrom, setValidatedFrom] = useState<unknown[]>()
  if (haveChanged(validatedFrom, validationSource)) {
    setValidatedFrom(validationSource)
    setBoundErrorText(current => nextErrorText(problem, displayError, current))
  }

  useEffect(() => {
    reportError(problem !== null)
  }, [visitMixPercentage, displayError, totalError, problem])

  const helperText =
    boundErrorText ||
    (mappedAppointmentTypes.length > 0
      ? `Includes: ${mappedAppointmentTypes.join(', ')}`
      : undefined)

  const input = (
    <Box>
      <FFInput
        error={!!boundErrorText}
        variant="outlined"
        disabled={disabled}
        onChange={event => {
          if (disabled) return
          const parsed = parseFloat(event.target.value)
          // Treat empty input as 0 to prevent NaN/null
          props.onChange(Number.isNaN(parsed) ? 0 : parsed)
        }}
        value={visitMixPercentage ?? ''}
        label={groupTitle}
        type="number"
        helperText={helperText}
        slotProps={{
          inputLabel: { shrink: true },
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
            inputProps: {
              min: 0,
              max: 100,
            },
          },
          formHelperText: {
            style: {
              whiteSpace: 'normal',
              lineHeight: 1.3,
            },
          },
        }}
      />
    </Box>
  )

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip} arrow>
        <span>{input}</span>
      </Tooltip>
    )
  }

  return input
}

export default VisitMixEditor
